import React from "react";
import { Icon } from "./registry";
/* ============================================================
   Fence Works — 3D Models gallery

   A live sample of the REAL fence engine (FenceBuild3D) — one
   panel/bay plus a gate, rendered per fence type with a small
   bit of configurability (type · height · color). NOT a new 3D
   build: it reuses the estimator's viewer verbatim.

   BUNDLE RULE: FenceBuild3D pulls three.js in statically, so it
   is ONLY ever imported via React.lazy — the same way quote.tsx
   and hub.tsx do — so the WebGL engine stays in its own chunk
   and never lands in the main bundle. (See fencebuild.tsx header.)
   ============================================================ */
const FenceBuild3DLazy: any = React.lazy(() => import("./fencebuild").then((m: any) => ({ default: m.FenceBuild3D })));

/* One sample per supported build kind. style/color mirror the engine's
   own PRESETS so each type renders in a sensible default material; the
   color chips below are the engine's real per-kind color names. */
const MODELS = [
  { key: "vinyl", label: "Vinyl Privacy", category: "Vinyl", style: "Solid Privacy", colors: ["White", "Tan", "Khaki", "Gray"] },
  { key: "wood", label: "Wood Privacy", category: "Wood", style: "Dog Ear Privacy", colors: ["Natural Cedar", "Redwood", "Cedar w/ Stain", "White"] },
  { key: "bob", label: "Board on Board", category: "Wood", style: "Board on Board", colors: ["Natural Cedar", "Redwood", "Cedar w/ Stain"] },
  { key: "shadowbox", label: "Shadow Box", category: "Wood", style: "Shadow Box", colors: ["Natural Cedar", "Redwood", "Cedar w/ Stain"] },
  { key: "chain", label: "Chain Link", category: "Chain Link", style: "Standard Chain Link", colors: ["Galvanized", "Black", "Green", "Brown"] },
  { key: "aluminum", label: "Aluminum", category: "Iron/Aluminum", style: "Flat Top (3-Rail)", colors: ["Black", "Bronze", "White", "Green"] },
];
const HEIGHTS = [4, 5, 6, 8];

function ModelsPage({ go }: any) {
  const [modelKey, setModelKey] = React.useState<any>(MODELS[0].key);
  const [heightFt, setHeightFt] = React.useState<any>(6);
  const [color, setColor] = React.useState<any>(MODELS[0].colors[0]);
  const model = MODELS.find((m) => m.key === modelKey) || MODELS[0];

  // switching type resets the color to that type's first valid swatch
  function pickModel(m: any) {
    setModelKey(m.key);
    setColor(m.colors[0]);
  }

  // a valid minimal spec — same shape the estimator/quote build (category,
  // style, color, heightFt, panelFt); no layout/quote → the engine renders
  // its generic straight sample (a few bays + a gate) in this style.
  const spec = React.useMemo(
    () => ({ category: model.category, style: model.style, color, heightFt, panelFt: 8 }),
    [model, color, heightFt]
  );

  return (
    <div className="page fade-up">
      <h1 className="display" style={{ fontSize: 30, margin: 0 }}>3D Models</h1>
      <p className="muted" style={{ margin: "5px 0 18px" }}>
        A live, to-scale sample of every fence type — a single panel and a matching gate, built by the same 3D engine your quotes use. Pick a style, height, and color to spin it around.
      </p>

      <div className="card pad" style={{ marginBottom: 16 }}>
        <div className="kicker" style={{ marginBottom: 10 }}>Fence type</div>
        <div className="row gap-8" style={{ flexWrap: "wrap", marginBottom: 16 }}>
          {MODELS.map((m) => (
            <button key={m.key} className={"filter-chip" + (modelKey === m.key ? " on" : "")} onClick={() => pickModel(m)}>{m.label}</button>
          ))}
        </div>

        <div className="row gap-16" style={{ flexWrap: "wrap" }}>
          <div>
            <div className="kicker" style={{ marginBottom: 8 }}>Height</div>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              {HEIGHTS.map((h) => (
                <button key={h} className={"filter-chip" + (heightFt === h ? " on" : "")} onClick={() => setHeightFt(h)}>{h}&prime;</button>
              ))}
            </div>
          </div>
          <div>
            <div className="kicker" style={{ marginBottom: 8 }}>Color</div>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              {model.colors.map((c: any) => (
                <button key={c} className={"filter-chip" + (color === c ? " on" : "")} onClick={() => setColor(c)}>{c}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card pad">
        {/* key on type forces a clean remount per style so the build resets to
            step 1; height/color flow through the spec without a remount. */}
        <React.Suspense fallback={<div className="muted" style={{ textAlign: "center", padding: "120px 0", fontSize: 13 }}><Icon name="layers" size={22} style={{ display: "block", margin: "0 auto 10px", color: "var(--teal)" }} />Loading the 3D engine…</div>}>
          <FenceBuild3DLazy key={model.key} spec={spec} />
        </React.Suspense>
      </div>
    </div>
  );
}

Object.assign(window, { ModelsPage });

export { ModelsPage, MODELS };
